import { ArrowUp } from "@tamagui/lucide-icons";
import type { DocumentPickerAsset } from "expo-document-picker";
import { ImagePickerAsset } from "expo-image-picker";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { GestureResponderEvent, Platform } from "react-native";
import {
  type TamaguiElement,
  TextArea as TamaguiTextArea,
  XStack,
  YStack,
  getTokenValue,
} from "tamagui";

import { AttachmentType } from "@/api/mutations/conversation";
import AttachmentPreview from "@/components/AttachmentPreview";
import AttachmentsButton from "@/components/AttachmentsButton";
import LoadingSpinner from "@/components/animated/LoadingSpinner";
import { Button } from "@/components/Button";
import type { InputKeyPressEvent } from "@/components/forms/Input";
import VoiceRecordButton from "@/components/VoiceRecordButton";
import { useAskUserQuestionsStore } from "@/store/askUserQuestions";
import { useConversationComposition } from "@/store/conversation";
import { useLiveRegionStore } from "@/store/liveRegion";
import fonts from "@/theme/fonts";
import { isMobileWeb } from "@/utils/web";
import VoiceVisualizer from "./animated/VoiceVisualizer";
import TalkToDoctorButton from "./TalkToDoctorButton";

interface ChatInputFieldProps {
  inputRef: React.RefObject<TamaguiElement | null>;
  stopAndSendRef: React.MutableRefObject<(() => Promise<void>) | undefined>;
  text: string;
  setText: (text: string) => void;
  height: number | undefined;
  placeholder: string | undefined;
  onFocus: () => void;
  onSubmitEditing: () => void;
  onContentSizeChange?: (e: any) => void;
  onKey: (key: string, shiftKey: boolean, preventDefault: () => void) => void;
  buttonDisabled: boolean;
  isPending: boolean;
  maxLength?: number;
  isDisabled?: boolean;
  attachments: AttachmentType[];
  onRemoveAttachment: (localUri: string) => void;
  onAttachmentsPicked: (
    assets: (DocumentPickerAsset | ImagePickerAsset)[],
    files: File[],
  ) => void;
  isUploading: boolean;
  showVoiceRecording?: boolean;
  conversationType?: string | null;
  noriConversationCount: number;
  onTalkToDoctor: () => void;
  hideTalkToDoctorButton?: boolean;
  hasContentToSend: boolean;
}

// Normalize key handling between web and native platforms.
// Web uses the real DOM onKeyDown, native uses onKeyPress
const keyEventProps = (
  onKey: (key: string, shiftKey: boolean, preventDefault: () => void) => void,
) =>
  Platform.OS === "web"
    ? {
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) =>
          onKey(e.key, e.shiftKey, () => e.preventDefault()),
      }
    : {
        onKeyPress: (e: InputKeyPressEvent) =>
          onKey(e.nativeEvent.key, Boolean(e.nativeEvent.shiftKey), () =>
            e.nativeEvent.preventDefault?.(),
          ),
      };

const ChatInputField = ({
  inputRef,
  stopAndSendRef,
  text,
  setText,
  height,
  onContentSizeChange,
  placeholder,
  onFocus,
  onSubmitEditing,
  onKey,
  buttonDisabled,
  isPending,
  maxLength,
  isDisabled = false,
  attachments,
  onRemoveAttachment,
  onAttachmentsPicked,
  isUploading,
  showVoiceRecording = true,
  conversationType,
  noriConversationCount,
  onTalkToDoctor,
  hideTalkToDoctorButton = false,
  hasContentToSend,
}: ChatInputFieldProps) => {
  const { isRecording, setIsRecording, isTranscribing } =
    useConversationComposition();
  const { announcePolite } = useLiveRegionStore();
  const prevRecording = useRef(false);
  const prevTranscribing = useRef(false);

  useEffect(() => {
    if (isRecording && !prevRecording.current) {
      announcePolite("Recording");
    } else if (!isRecording && prevRecording.current && !isTranscribing) {
      announcePolite("Recording stopped");
    }
    prevRecording.current = isRecording;
  }, [isRecording, isTranscribing, announcePolite]);

  useEffect(() => {
    if (isTranscribing && !prevTranscribing.current) {
      announcePolite("Transcribing");
    } else if (!isTranscribing && prevTranscribing.current) {
      announcePolite("Transcription complete");
    }
    prevTranscribing.current = isTranscribing;
  }, [isTranscribing, announcePolite]);

  const { session: askUserQuestionsSession } = useAskUserQuestionsStore();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const textAreaProps = {
    "aria-label": "Chat input",
    ref: inputRef,
    unstyled: true,
    flex: 1,
    fontSize: "$md",
    lineHeight: "$md",
    minHeight: fonts["sans-serif"].lineHeight.md,
    fontFamily: "$sans-serif",
    focusStyle: { outlineWidth: 0 },
    paddingVertical: "$100",
    alignSelf: "center",
    height,
    maxHeight: fonts["sans-serif"].lineHeight.md * 7,
    multiline: true,
    rows: 1, // Browser defaults textarea to rows=2, so we set it explicitly
    numberOfLines: 1,
    placeholder: isRecording
      ? "Recording..."
      : isTranscribing
        ? "Transcribing..."
        : placeholder,
    onFocus,
    onContentSizeChange,
    ...keyEventProps(onKey),
    onChangeText: setText,
    value: text,
    maxLength,
    overflowWrap: "break-word",
    wordBreak: "break-word",
    editable: !isDisabled && !isRecording && !isTranscribing,
    ...(isDisabled ? { disabled: true } : {}),
    placeholderTextColor: isDisabled ? "$textDisabled" : "$placeholderColor",
    color: isDisabled ? "$textDisabled" : undefined,
    opacity: isDisabled ? 0.7 : 1,
  } as const;

  const buttonProps = {
    padding: "$50",
    marginVertical: "$100",
    onPress: (e: GestureResponderEvent) => {
      e.stopPropagation();
      // Blur to dismiss keyboard everywhere except desktop web
      const isDesktopWeb = Platform.OS === "web" && !isMobileWeb();
      if (!isDesktopWeb) {
        inputRef.current?.blur?.();
      }
      onSubmitEditing();
    },
    "aria-label": "Send message",
    round: true,
    backgroundColor:
      isDisabled || buttonDisabled ? "$backgroundGround" : "$fillButtonPrimary",
    disabled: buttonDisabled,
  };

  const buttonContent = isPending ? (
    <LoadingSpinner size={getTokenValue("$250")} />
  ) : (
    <ArrowUp
      size="$250"
      color={buttonDisabled ? "$textPrimary" : "$textButtonPrimary"}
    />
  );

  return (
    <YStack>
      <AttachmentPreview
        attachments={attachments}
        onRemoveAttachment={onRemoveAttachment}
      />
      <XStack paddingHorizontal="$100" gap="$50" alignItems="flex-end">
        <AttachmentsButton
          disabled={isPending || isRecording || isDisabled}
          isUploading={isUploading}
          onAttachmentsPicked={onAttachmentsPicked}
          conversationType={conversationType}
        />
        <XStack flex={1} position="relative" alignSelf="center">
          <TamaguiTextArea
            {...textAreaProps}
            opacity={isRecording ? 0 : 1}
            pointerEvents={isRecording ? "none" : "auto"}
          />
          {isRecording && (
            <XStack
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              alignItems="center"
            >
              <VoiceVisualizer stream={stream} />
            </XStack>
          )}
        </XStack>

        {hasContentToSend ? (
          <Button.Wrapper {...buttonProps}>{buttonContent}</Button.Wrapper>
        ) : (
          <>
            {!hideTalkToDoctorButton && (
              <TalkToDoctorButton
                onPress={onTalkToDoctor}
                disabled={
                  isPending || isDisabled || isRecording || isTranscribing
                }
              />
            )}

            {showVoiceRecording && (
              <VoiceRecordButton
                disabled={isPending || isDisabled || !!askUserQuestionsSession}
                onRecordingStateChange={setIsRecording}
                onStreamChange={setStream}
                onStopAndSendRef={stopAndSendRef}
              />
            )}
          </>
        )}
      </XStack>
    </YStack>
  );
};

export default ChatInputField;
